import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaShoppingCart, FaEye, FaHeart, FaRegHeart } from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { toast } from 'react-toastify';
import { generateSlug } from '../../utils/urlUtils';

const Card = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.03) 100%);
  border-radius: 16px;
  padding: 1.2rem;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
  height: 100%;
  display: ${props => props.viewMode === 'list' ? 'grid' : 'block'};
  grid-template-columns: ${props => props.viewMode === 'list' ? '180px 1fr auto' : 'none'};
  grid-gap: ${props => props.viewMode === 'list' ? '1.5rem' : '0'};
  align-items: ${props => props.viewMode === 'list' ? 'center' : 'stretch'};
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(10px);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(74, 144, 226, 0.05) 0%, rgba(74, 144, 226, 0) 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
    border-color: rgba(74, 144, 226, 0.4);
    
    &::before {
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: ${props => props.viewMode === 'list' ? '120px 1fr' : 'none'};
    padding: 1rem;
    border-radius: 14px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: ${props => props.viewMode === 'list' ? '100px 1fr' : 'none'};
    grid-gap: ${props => props.viewMode === 'list' ? '0.75rem' : '0'};
    padding: 0.85rem;
    border-radius: 12px;
  }
`;

const ProductImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 14px;
  margin-bottom: ${props => props.viewMode === 'list' ? '0' : '1.2rem'};
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(0deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 30%, rgba(0,0,0,0) 60%);
    pointer-events: none;
    transition: opacity 0.3s ease;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(74, 144, 226, 0.3) 0%, rgba(74, 144, 226, 0) 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: 1;
    pointer-events: none;
  }
  
  &:hover {
    &::before {
      opacity: 1;
    }
    
    img {
      transform: scale(1.1);
    }
  }
  
  @media (max-width: 768px) {
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    border-radius: 10px;
    margin-bottom: ${props => props.viewMode === 'list' ? '0' : '0.8rem'};
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: ${props => props.viewMode === 'list' ? '130px' : '220px'};
  object-fit: cover;
  border-radius: 14px;
  transition: transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  filter: brightness(0.95);
  
  @media (max-width: 768px) {
    height: ${props => props.viewMode === 'list' ? '120px' : '180px'};
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    height: ${props => props.viewMode === 'list' ? '100px' : '160px'};
    border-radius: 10px;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  ${props => props.viewMode === 'list' && `
    height: 100%;
    justify-content: space-between;
  `}
`;

const ProductTitle = styled.h3`
  color: white;
  margin: 0.6rem 0 0.4rem;
  font-size: 1.2rem;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  cursor: pointer;
  font-weight: 600;
  transition: color 0.3s ease;
  letter-spacing: 0.02em;
  
  &:hover {
    color: #4a90e2;
    transform: translateX(2px);
  }
  
  @media (max-width: 768px) {
    font-size: 1.05rem;
    margin: 0.5rem 0 0.3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin: 0.4rem 0 0.2rem;
    -webkit-line-clamp: ${props => props.viewMode === 'list' ? '1' : '2'};
  }
`;

const ProductDescription = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin: 0.5rem 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  display: ${props => props.viewMode === 'list' ? 'block' : 'none'};
  line-height: 1.4;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin: 0.3rem 0;
    -webkit-line-clamp: 1;
  }
`;

const ProductPrice = styled.p`
  font-weight: bold;
  font-size: 1.3rem;
  margin: 0.7rem 0 0.5rem;
  background: linear-gradient(90deg, #4a90e2, #63b3ed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 40px;
    height: 2px;
    background: linear-gradient(90deg, #4a90e2, transparent);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.15rem;
    margin: 0.5rem 0 0.3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.05rem;
    margin: 0.4rem 0 0.2rem;
  }
`;

const ArtistName = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin: 0.5rem 0;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  width: fit-content;
  
  &:hover {
    color: #4a90e2;
    transform: translateX(2px);
  }
  
  &::before {
    content: 'By ';
    color: rgba(255, 255, 255, 0.5);
    margin-right: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 0.7rem;
  flex-wrap: wrap;
  justify-content: ${props => props.viewMode === 'list' ? 'flex-end' : 'space-between'};
  align-items: center;
  margin-top: auto;
  padding-top: 0.5rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    flex-direction: ${props => props.viewMode === 'list' ? 'column' : 'row'};
    align-items: ${props => props.viewMode === 'list' ? 'flex-end' : 'center'};
    padding-top: 0.3rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.4rem;
  }
`;

const Button = styled(motion.button)`
  background: ${props => props.primary ? 'linear-gradient(90deg, #4a90e2, #357abd)' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border: none;
  padding: 0.6rem;
  border-radius: 12px;
  flex: ${props => props.flex || '1'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  letter-spacing: 0.02em;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    background: ${props => props.primary ? 'linear-gradient(90deg, #357abd, #2a6db2)' : 'rgba(255, 255, 255, 0.15)'};
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
    transform: translateY(-2px);
    
    &::before {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.5rem;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.4rem;
    gap: 0.3rem;
    border-radius: 8px;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${props => props.type === 'new' ? 'linear-gradient(90deg, #4caf50, #388e3c)' : 
               props.type === 'sale' ? 'linear-gradient(90deg, #f44336, #d32f2f)' : 
               'linear-gradient(90deg, #ff9800, #f57c00)'};
  color: white;
  padding: 0.3rem 0.7rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  z-index: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 480px) {
    padding: 0.2rem 0.5rem;
    font-size: 0.7rem;
    top: 0.7rem;
    right: 0.7rem;
  }
`;

const WishlistButton = styled(motion.button)`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: rgba(0, 0, 0, 0.5);
  color: ${props => props.active ? '#ff6b6b' : 'white'};
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  backdrop-filter: blur(2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: ${props => props.active ? 'rgba(255, 107, 107, 0.2)' : 'rgba(0, 0, 0, 0.7)'};
    transform: scale(1.1);
  }
  
  @media (max-width: 480px) {
    width: 30px;
    height: 30px;
    top: 0.7rem;
    left: 0.7rem;
  }
`;

const AddedToCartOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  gap: 1rem;
  z-index: 2;
  backdrop-filter: blur(3px);
  border-radius: 12px;
  
  svg {
    filter: drop-shadow(0 0 8px rgba(74, 144, 226, 0.6));
    color: #4a90e2;
  }
  
  p {
    font-weight: 600;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }
`;

const ActionButtons = styled.div`
  ${props => props.viewMode === 'list' && `
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    
    @media (max-width: 768px) {
      display: none;
    }
  `}
`;

const ProductCard = ({ product, artist, viewMode = 'grid' }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [showAddedMessage, setShowAddedMessage] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  
  // Extract product data
  const {
    $id,
    name,
    description,
    price,
    imageUrl,
    isNew = false
  } = product || {};
  
  const handleAddToCart = (e) => {
    e.stopPropagation();
    
    addToCart({
      id: $id,
      name,
      price,
      imageUrl,
      sellerId: product.sellerId,
      quantity: 1
    });
    
    toast.success(`${name} added to cart`);
    setShowAddedMessage(true);
    setTimeout(() => {
      setShowAddedMessage(false);
    }, 1500);
  };
  
  const handleViewDetails = () => {
    // Create SEO-friendly URL with product name and ID
    const slug = `${generateSlug(name)}-${$id}`;
    navigate(`/marketplace/products/${slug}`);
  };
  
  const handleArtistClick = (e) => {
    e.stopPropagation();
    if (artist && artist.$id) {
      navigate(`/marketplace/artist/${artist.$id}`);
    }
  };
  
  const toggleWishlist = (e) => {
    e.stopPropagation();
    setInWishlist(!inWishlist);
    
    if (!inWishlist) {
      toast.success(`${name} added to wishlist`);
    } else {
      toast.info(`${name} removed from wishlist`);
    }
  };

  return (
    <Card 
      whileHover={{ y: -5 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleViewDetails}
      viewMode={viewMode}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <ProductImageContainer viewMode={viewMode}>
        {isNew && <Badge type="new">New</Badge>}
        <WishlistButton 
          onClick={toggleWishlist}
          whileTap={{ scale: 0.9 }}
          active={inWishlist}
        >
          {inWishlist ? <FaHeart /> : <FaRegHeart />}
        </WishlistButton>
        {imageUrl && (
          <ProductImage 
            src={imageUrl} 
            alt={name} 
            viewMode={viewMode}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/placeholder-product.jpg';
            }}
          />
        )}
      </ProductImageContainer>
      
      <ProductInfo viewMode={viewMode}>
        <div>
          <ProductTitle onClick={handleViewDetails} viewMode={viewMode}>{name}</ProductTitle>
          <ProductDescription viewMode={viewMode}>{description}</ProductDescription>
          <ProductPrice>UGX {parseFloat(price).toLocaleString('en-UG')}</ProductPrice>
          {artist && (
            <ArtistName onClick={handleArtistClick}>
              {artist.displayName || artist.email?.split('@')[0] || 'Artist'}
            </ArtistName>
          )}
        </div>
        
        {viewMode !== 'list' && (
          <ButtonsContainer>
            <Button 
              primary
              onClick={handleAddToCart}
              whileTap={{ scale: 0.95 }}
            >
              <FaShoppingCart /> Add to Cart
            </Button>
            <Button 
              flex="0.5"
              onClick={handleViewDetails}
              whileTap={{ scale: 0.95 }}
            >
              <FaEye />
            </Button>
          </ButtonsContainer>
        )}
      </ProductInfo>
      
      {viewMode === 'list' && (
        <ActionButtons viewMode={viewMode}>
          <Button 
            primary
            onClick={handleAddToCart}
            whileTap={{ scale: 0.95 }}
          >
            <FaShoppingCart /> Add to Cart
          </Button>
          <Button 
            onClick={handleViewDetails}
            whileTap={{ scale: 0.95 }}
          >
            <FaEye /> View Details
          </Button>
        </ActionButtons>
      )}
      
      {showAddedMessage && (
        <AddedToCartOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <FaShoppingCart size={30} />
          <p>Added to cart!</p>
        </AddedToCartOverlay>
      )}
    </Card>
  );
};

export default ProductCard;
